import { useMutation, useQuery } from "@apollo/client";
import { Field, FinalFormInput } from "@comet/admin";
import { Delete } from "@mui/icons-material";
import { Button, CircularProgress } from "@mui/material";
import { ErrorComponent } from "@src/components";
import Form from "@src/components/Form";
import {
    type GQLActivityAdminQuery,
    type GQLActivityAdminQueryVariables,
    type GQLDeleteActivityAdminMutation,
    type GQLDeleteActivityAdminMutationVariables,
    type GQLUpdateActivityAdminMutation,
    type GQLUpdateActivityAdminMutationVariables,
    type GQLUpdateActivityInput,
} from "@src/graphql.generated";
import { type FC, type PropsWithChildren } from "react";
import { FormattedMessage, useIntl } from "react-intl";

// eslint-disable-next-line @comet/no-private-sibling-import
import { activityQuery, deleteActivityMutation, updateActivityMutation } from "./ActivityLog.gql";
import FormButtons from "./FormButtons";

interface EditFormProps {
    handleClose: () => void;
    id: string;
}

const EditForm: FC<PropsWithChildren<EditFormProps>> = ({ handleClose, id }) => {
    const intl = useIntl();
    const { data, error, loading } = useQuery<GQLActivityAdminQuery, GQLActivityAdminQueryVariables>(activityQuery, { variables: { id } });
    const [updateMutation] = useMutation<GQLUpdateActivityAdminMutation, GQLUpdateActivityAdminMutationVariables>(updateActivityMutation);
    const [deleteMutation] = useMutation<GQLDeleteActivityAdminMutation, GQLDeleteActivityAdminMutationVariables>(deleteActivityMutation);

    if (error) {
        return <ErrorComponent />;
    }

    if (loading || !data) {
        return <CircularProgress />;
    }

    const submit = ({ message, createdBy, cameraId }: GQLUpdateActivityInput) =>
        updateMutation({
            variables: {
                id: data.activityAdmin.id,
                input: {
                    message,
                    createdBy,
                    cameraId,
                },
            },
            refetchQueries: ["ActivitiesAdmin"],
        });

    const handleDelete = () =>
        deleteMutation({
            variables: {
                id: data.activityAdmin.id,
            },
            refetchQueries: ["ActivitiesAdmin"],
        }).then(handleClose);

    return (
        <Form
            mode="edit"
            onSubmit={submit}
            title={intl.formatMessage({ id: "reviermanager.devices.form.activityLog.edit", defaultMessage: "Aktivität bearbeiten" })}
            onAfterSubmit={handleClose}
            initialValues={data.activityAdmin}
            customButtons={<FormButtons handleCancelClick={handleClose} />}
            link={
                <Button color="primary" variant="text" onClick={handleDelete} startIcon={<Delete />}>
                    <FormattedMessage id="reviermanager.camera.log.delete" defaultMessage="Löschen" />
                </Button>
            }
        >
            <Field
                name="message"
                component={FinalFormInput}
                label={intl.formatMessage({ id: "reviermanager.devices.form.activityLog.message", defaultMessage: "Notiz" })}
                rows={4}
                multiline
                fullWidth
                required
            />
        </Form>
    );
};

export default EditForm;
